import React, { useContext } from 'react';
import { useSearchParams } from 'react-router-dom';
import Paper from '@mui/material/Paper';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import useMediaQuery from '@mui/material/useMediaQuery';
import { RootContextType, ProfilePageProps, TabItem, TabsObject } from '../../types';
import RootContext from '../../services/context-states/root-context';
import { Tabs } from '../../elements';
import { AccountInfo, Payments, Policies, UserManagement } from './views';
import { useTheme } from '@mui/material/styles';
const ProfilePage: React.FC<ProfilePageProps> = () => {
    const { messageContext, accessFlagsContext, userContext } = useContext<RootContextType>(RootContext);
    const { setError } = messageContext  ;
    const { accessFlags } = accessFlagsContext;
    const { currentUser } = userContext;
    const [searchParams] = useSearchParams();

    const tabToShow = searchParams.get('show');

    const getTabsToLoad = (): TabsObject => {
        const tabsToEvaluate: TabItem[] = [
            { key: 0, name: 'Account Info', view: <AccountInfo profile={currentUser!} />, display: true, showBadge: false, badgeValue: -1 },
            { key: 1, name: 'Billing', view: <Payments />, display: accessFlags.enableManageBilling, showBadge: accessFlags.showIncompleteProfile, badgeValue: -1 },
            { key: 2, name: 'User Management', view: <UserManagement />, display: accessFlags.enableManageUsers, showBadge: accessFlags.showUnusedLicenses, badgeValue: -1 },
            { key: 3, name: 'Policies', view: <Policies />, display: true, showBadge: false, badgeValue: -1 }
        ];

        const tabsToReturn: TabsObject = {};
        let tabIndex = 0;

        tabsToEvaluate.forEach((tab) => {
            if (tab.display) {
                tabsToReturn[tabIndex] = tab;
                tabIndex++;
            }
        });

        return tabsToReturn;
    };

    const getInitialTab = (tabs: TabsObject): number => {
        if (tabToShow) {
            const displayIndex = Object.keys(tabs).find(key => tabs[Number(key)].name === tabToShow);
            if (displayIndex) {
                return parseInt(displayIndex);
            }
        }
        return 0;
    };

    const tabsToLoad = getTabsToLoad();
    const initialTabKey = getInitialTab(tabsToLoad);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

    // Reset the URL since initalTabKey is set
    window.history.replaceState({}, document.title, '/settings');

    setError(!currentUser ? 'We are unable to locate your information at this time' : '');

    return (
        <Container maxWidth="lg" sx={{ py: { xs: 1, sm: 2, md: 3 }, px: { xs: 1, sm: 2 } }}>
            <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-start',
                minHeight: { xs: 'auto', sm: 'calc(100vh - 120px)', md: 'calc(100vh - 140px)' }
            }}>
                <Paper
                    elevation={isMobile ? 0 : 3}
                    sx={{
                        background: theme.palette.container.background,
                        width: '100%',
                        maxWidth: { xs: '100%', md: '900px' },
                        minHeight: { xs: 'auto', sm: '600px', md: '700px' },
                        maxHeight: { xs: 'none', md: '700px' },
                        overflow: { xs: 'visible', md: 'auto' }
                    }}
                >
                    <Tabs
                        styles={{
                            height: '100%',
                            '& .MuiTabs-root': {
                                minHeight: { xs: 40, sm: 48 },
                            },
                            '& .MuiTab-root': {
                                minHeight: { xs: 40, sm: 48 },
                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                padding: { xs: '8px 12px', sm: '12px 16px' },
                                minWidth: { xs: 'auto', sm: 90 }
                            }
                        }}
                        tabsObj={tabsToLoad}
                        initialSelectedTab={initialTabKey}
                    />
                </Paper>
            </Box>
        </Container>
    );
};

export default ProfilePage; 